import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { PropAsset } from "building-types/props/PropAsset";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import { PropBoundOrientation } from "building-types/props/propTypesEnum";
import { WallType } from "building-types/walls/wallEnums";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallStrip } from "building-types/walls/wallStrip";
import type { Wall, WallPath } from "building-types/walls/wallTypes";
import { GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import { assertIsNotNull, ensureDefined } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import { mergedPropLibraryNew } from "doc/propTools";
import { aabb2ByPolyLine2s, aabb2Center, aabb2Scale } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import type { ClosestPointToPolyLine2 } from "geom/closestPoint";
import { closestPointPolyLine2 } from "geom/closestPoint";
import { classifyContainmentVec2PolyLine2Fast } from "geom/containment";
import { ANGLE_EPSILON } from "geom/core";
import { offsetPolyLine2 } from "geom/offset";
import {
  polyLine2Centroid,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2EdgeCount,
  polyLine2EdgeEval,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2Rotate,
  polyLine2ScaleX,
  polyLine2ScaleY,
  polyLine2Shift,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { rect2ByAABB2 } from "geom/rect2";
import type { Vec2 } from "geom/vec2";
import { vec2Copy, vec2DistSquared, vec2Dot, vec2IsInEdgeBounds, vec2Len, vec2Rotate, vec2Sub } from "geom/vec2";
import { getWallPath } from "../elements/walls/wall";
import { solidWallSubstrateLayer } from "../elements/walls/wallLayers";

interface PropBounds {
  readonly aabb: AABB2;
  readonly rect: PolyLine2;
  readonly origin: Vec2;
  readonly normal: Vec2;
  readonly isLong: boolean;
}

interface PropRoomInfo {
  readonly roomEdgeIndex: number;
  readonly roomEdgePt: Vec2;
  readonly roomEdgeDistance: number;
  readonly roomEdgeWallCenterLine: PolyLine2;
  readonly roomEdgeWallBaseLine: PolyLine2;
  readonly roomEdgeWallThickness: number;
  readonly roomEdgeWallPt: Vec2;
}

export interface PropLocationOnPerimeter {
  readonly prop: Prop;
  readonly propBounds: PropBounds;
  readonly propRoomInfo: PropRoomInfo;
  readonly isEdgeWall: boolean;
  readonly isEdgeWallStrip: boolean;
  readonly isFreeProp: boolean;
  readonly isDrawOnly: boolean;
  readonly perimeterIndex: number;
}

interface PerimeterIntersection {
  readonly origin: Vec2;
  readonly perimeterEdgeIntersection: ClosestPointToPolyLine2;
  readonly perimeterIndex: number;
  readonly source?: PropLocationOnPerimeter;
}

type PerimeterEdgeIntersectionSet = {
  [edgeIndex: number]: PerimeterIntersection[];
};

type PerimeterIntersectionSet = {
  [perimeterIndex: number]: PerimeterEdgeIntersectionSet;
};

export async function makePropLocations(
  building: Pick<Building, "settings" | "wallMasses"> & { doc: Pick<Doc, "assets"> },
  props: Prop[],
  perimeterProfiles: PolyLine2[],
): Promise<PropLocationOnPerimeter[]> {
  const { settings, doc } = building;

  const propCatalog = settings.props;
  const mergedPropLibrary = mergedPropLibraryNew(doc.assets);
  const propLocations: PropLocationOnPerimeter[] = [];

  for (const prop of props) {
    const propAssetId = propCatalog[prop.src]?.propAssetId;
    if (propAssetId === undefined) {
      continue;
    }

    const propAsset = await mergedPropLibrary.get(propAssetId);
    if (!propAsset) {
      continue;
    }

    const propLocation = propLocationNew({ prop, propAsset, building, perimeterProfiles });
    if (!propLocation) {
      continue;
    }

    propLocations.push(propLocation);
  }
  return propLocations;
}

export function makePropPerimeterIntersections(
  propLocations: PropLocationOnPerimeter[],
  points: Vec2[],
  perimeterProfiles: PolyLine2[],
  fromWallEdge: boolean = false,
  filter?: (x1: number, y1: number, x2: number, y2: number) => boolean,
) {
  const interiorIntersectionsByPerimeter: PerimeterIntersectionSet = {};
  const exteriorIntersectionsByPerimeter: PerimeterIntersectionSet = {};

  for (const propLocation of propLocations) {
    const propPerimeterIndex = propLocation.perimeterIndex;
    const perimeterTarget = perimeterProfiles[propPerimeterIndex];

    if (propLocation.isDrawOnly) {
      continue;
    }

    calculatePerimeterIntersectionsFromPropLocation(
      interiorIntersectionsByPerimeter,
      exteriorIntersectionsByPerimeter,
      perimeterTarget,
      propPerimeterIndex,
      propLocation,
      fromWallEdge,
      filter,
    );
  }

  for (const point of points) {
    const loadPerimeterIndex = findPerimeterIndex(point[0], point[1], perimeterProfiles);
    if (loadPerimeterIndex === -1) {
      continue;
    }

    const loadLocation = vec2Copy(point);
    const perimeterTarget = perimeterProfiles[loadPerimeterIndex];

    calculatePerimeterIntersectionsFromPointLocation(
      interiorIntersectionsByPerimeter,
      exteriorIntersectionsByPerimeter,
      perimeterTarget,
      loadPerimeterIndex,
      loadLocation,
      false,
      undefined,
      filter,
    );
  }

  return { interiorIntersectionsByPerimeter, exteriorIntersectionsByPerimeter };
}

interface PropLocationParams {
  prop: Prop;
  propAsset: PropAsset;
  building: Pick<Building, "settings" | "wallMasses">;
  freeLocationThreshold?: number; // there is definitely a better way to do this
  perimeterProfiles: PolyLine2[];
}

function propLocationNew({
  prop,
  propAsset,
  building,
  freeLocationThreshold = 8,
  perimeterProfiles,
}: PropLocationParams): PropLocationOnPerimeter | null {
  const { room } = prop;
  const { wallMasses, settings } = building;

  assertIsNotNull(room);

  const propCatalog = settings.props;

  const { wallIndex } = wallMasses[room.level.id];

  const propBounds = createPropBounds(prop, propAsset, propCatalog);
  const propRoomInfoWithWall = createPropRoomInfo(prop, propBounds, wallIndex);
  if (!propRoomInfoWithWall) {
    return null;
  }

  const {
    wall,
    wallStrip,
    info: { roomEdgeDistance },
  } = propRoomInfoWithWall;

  const wallStripPerimeterEdgeWalls = findWallsOnPerimeterEdge(wallStrip, [wall]);

  const isDrawOnly = propCatalog[prop.src]?.excludeFromFoundationPlanDimensions;
  const isEdgeWall = isWallOnPerimeterEdge(wall);
  const isFreeProp = roomEdgeDistance > freeLocationThreshold;

  let isEdgeWallStrip = isEdgeWall;

  if (!isEdgeWallStrip) {
    for (const edgeWall of wallStripPerimeterEdgeWalls) {
      // edge is connected to a perimeter edge
      if (areWallPathsConnected(getWallPath(wall), getWallPath(edgeWall))) {
        isEdgeWallStrip = true;
        break;
      }

      // edge is 90 degrees to a perimeter edge on this wall strip
      if (areWallPathsRectilinear(getWallPath(wall), getWallPath(edgeWall))) {
        isEdgeWallStrip = true;
        break;
      }
    }
  }

  const [propX, propY] = propRoomInfoWithWall.info.roomEdgePt;
  // use the room edge point since some props are outside the perimeter edge
  const perimeterIndex = findPerimeterIndex(propX, propY, perimeterProfiles);
  if (perimeterIndex === -1) {
    return null;
  }

  return {
    prop,
    propBounds,
    propRoomInfo: propRoomInfoWithWall.info,
    isEdgeWall,
    isEdgeWallStrip,
    isFreeProp,
    isDrawOnly: !!isDrawOnly,
    perimeterIndex,
  };
}

function createPropBounds(prop: Prop, propAsset: PropAsset, propCatalog: PropAttributeTable): PropBounds {
  const { minX, minY, maxX, maxY } = propAsset.boundingBox;

  let aabb = aabb2New(minX, minY, maxX, maxY);
  aabb = aabb2Scale(aabb, prop.scaleX * GLTF_ASSET_SCALE);

  let aabbRotation = degreeToRadian(prop.rotationDegrees);
  let aabbRect = rect2ByAABB2(aabb);

  aabbRect = polyLine2ScaleX(aabbRect, prop.scaleX);
  aabbRect = polyLine2ScaleY(aabbRect, prop.scaleY);

  aabbRect = polyLine2Move(aabbRect, prop.x, prop.y);

  if (prop.scaleX < 0) {
    aabbRotation += Math.PI;
    aabbRect = polyLine2Shift(polyLine2Reverse(aabbRect), -1);
  }

  aabbRect = polyLine2Rotate(aabbRect, aabbRotation, prop.x, prop.y);

  const longProp = !!(propCatalog[prop.src]?.propBoundOrientation === PropBoundOrientation.LONG_PROP);
  const centerProp = !!(propCatalog[prop.src]?.propBoundOrientation === PropBoundOrientation.CENTER_PROP);
  const centroidProp = !!(propCatalog[prop.src]?.propBoundOrientation === PropBoundOrientation.CENTROID_PROP);

  let aabbOrigin: Vec2;
  let aabbNormal: Vec2;

  if (centerProp) {
    aabbOrigin = [prop.x, prop.y];
    aabbNormal = prop.scaleY < 0 ? [0, -1] : [0, 1];
  } else if (longProp) {
    const [ox, oy] = polyLine2EdgeEval(aabbRect, 3, 0.5, true);
    aabbOrigin = [ox, oy];
    aabbNormal = prop.scaleX < 0 ? [-1, 0] : [1, 0];
  } else {
    const [ox, oy] = polyLine2EdgeEval(aabbRect, 2, 0.5, true);
    aabbOrigin = [ox, oy];
    aabbNormal = prop.scaleY < 0 ? [0, -1] : [0, 1];
  }

  if (centroidProp) {
    [aabbOrigin[0], aabbOrigin[1]] = ensureDefined(polyLine2Centroid(aabbRect));
  }

  aabb = ensureDefined(aabb2ByPolyLine2s([aabbRect]));
  [aabbNormal[0], aabbNormal[1]] = vec2Rotate(aabbNormal[0], aabbNormal[1], aabbRotation);

  return {
    aabb,
    rect: aabbRect,
    origin: aabbOrigin,
    normal: aabbNormal,
    isLong: longProp,
  };
}

function createPropRoomInfo(
  prop: Prop,
  propBounds: PropBounds,
  wallIndex: WallIndex,
): { info: PropRoomInfo; wall: Wall; wallStrip: WallStrip } | null {
  const { room } = prop;
  const { origin } = propBounds;

  assertIsNotNull(room);

  // ultimately this should be replaced with metadata on the plumbing props about plumbing info
  let closestRoomEdgePoint = closestPointPolyLine2(room.outline, origin[0], origin[1]);
  const roomEdgeIndex = closestRoomEdgePoint.edgeIndex;

  const wall = wallIndex.wallFromRoomEdge(room, roomEdgeIndex);
  if (!wall) {
    return null;
  }
  const wallStrip = wallIndex.wallStripFromRoomEdgeIndex(room, roomEdgeIndex);
  if (!wallStrip) {
    return null;
  }

  let roomEdgeWallCenterLine: PolyLine2;
  let roomEdgeWallBaseLine: PolyLine2;
  let roomEdgeWallThickness = 0;
  if (wall.wallType !== WallType.VOID) {
    const mass = wall.wallType === WallType.SOLID ? wall.mass : wall;
    const structuralMass = solidWallSubstrateLayer(mass);

    roomEdgeWallThickness = structuralMass.thickness;
    roomEdgeWallBaseLine = wallStrip.baseline();
    roomEdgeWallCenterLine = offsetPolyLine2(polyLine2Copy(roomEdgeWallBaseLine), roomEdgeWallThickness / 2);
    closestRoomEdgePoint = closestPointPolyLine2(roomEdgeWallCenterLine, origin[0], origin[1]);
  } else {
    roomEdgeWallBaseLine = wallStrip.baseline();
    roomEdgeWallCenterLine = wallStrip.centerLine();
  }

  const roomEdgePt: Vec2 = [closestRoomEdgePoint.x, closestRoomEdgePoint.y];
  const roomEdgeDistance = vec2Len(origin[0] - roomEdgePt[0], origin[1] - roomEdgePt[1]);
  const roomEdgeWallCpt = closestPointPolyLine2(roomEdgeWallBaseLine, roomEdgePt[0], roomEdgePt[1]);
  const roomEdgeWallPt = [roomEdgeWallCpt.x, roomEdgeWallCpt.y];

  return {
    wall,
    wallStrip,
    info: {
      roomEdgeIndex,
      roomEdgePt,
      roomEdgeDistance,
      roomEdgeWallCenterLine,
      roomEdgeWallBaseLine,
      roomEdgeWallThickness,
      roomEdgeWallPt,
    },
  };
}

function calculatePerimeterIntersectionsFromPropLocation(
  interiorIntersections: PerimeterIntersectionSet,
  exteriorIntersections: PerimeterIntersectionSet,
  perimeterProfile: PolyLine2,
  perimeterIndex: number,
  propLocation: PropLocationOnPerimeter,
  useWallPt: boolean = false,
  filter?: (x1: number, y1: number, x2: number, y2: number) => boolean,
) {
  const {
    propBounds,
    propRoomInfo,
    isFreeProp,
    isEdgeWallStrip,
    isEdgeWall,
    prop: { alignmentX, alignmentY, useAlignmentCalc },
  } = propLocation;
  const { roomEdgePt, roomEdgeWallPt } = propRoomInfo;
  const { origin: aabbOrigin } = propBounds;

  let origin: Vec2 = useAlignmentCalc ? [ensureDefined(alignmentX), ensureDefined(alignmentY)] : aabbOrigin;

  if (!isFreeProp) {
    if (useWallPt) {
      origin = roomEdgeWallPt;
    } else {
      origin = roomEdgePt;
    }
  }

  const allowExteriorIntersections = isEdgeWall || isEdgeWallStrip;

  // special case where we are only interested in the exterior dim
  if (isEdgeWall && !isFreeProp) {
    const intersection = findClosestBoundedEdgeIntersection(origin, perimeterProfile, filter);
    if (!intersection) {
      return;
    }

    addPerimeterEdgeIntersectionToSet(exteriorIntersections, perimeterIndex, intersection, origin);
  } else {
    calculatePerimeterIntersectionsFromPointLocation(
      interiorIntersections,
      exteriorIntersections,
      perimeterProfile,
      perimeterIndex,
      origin,
      allowExteriorIntersections,
      propLocation,
      filter,
    );
  }
}

function calculatePerimeterIntersectionsFromPointLocation(
  interiorIntersections: PerimeterIntersectionSet,
  exteriorIntersections: PerimeterIntersectionSet,
  perimeterProfile: PolyLine2,
  perimeterIndex: number,
  origin: Vec2,
  allowExteriorIntersections: boolean,
  source?: PropLocationOnPerimeter,
  filter?: (x1: number, y1: number, x2: number, y2: number) => boolean,
) {
  const baseEdgeX = findClosestBoundedEdgeIntersection(origin, perimeterProfile, filter);
  if (!baseEdgeX) {
    return;
  }

  addPerimeterEdgeIntersectionToSet(interiorIntersections, perimeterIndex, baseEdgeX, origin, source);

  const nextEdgeX = findNextClosestBoundedEdgeIntersection(origin, baseEdgeX.edgeIndex, perimeterProfile, filter);
  const baseEdgeDist = vec2Len(origin[0] - baseEdgeX.x, origin[1] - baseEdgeX.y);
  const nextEdgeDist = nextEdgeX ? nextEdgeX.dist : Number.POSITIVE_INFINITY;

  // if the distance to the base edge is shorter than the distance to the next edge
  // add an exterior dim instead
  if (allowExteriorIntersections && nextEdgeDist > baseEdgeDist) {
    addPerimeterEdgeIntersectionToSet(exteriorIntersections, perimeterIndex, baseEdgeX, origin, source);
  } else if (nextEdgeX) {
    addPerimeterEdgeIntersectionToSet(interiorIntersections, perimeterIndex, nextEdgeX, origin, source);
  }
}

function addPerimeterEdgeIntersectionToSet(
  targetSet: PerimeterIntersectionSet,
  perimeterIndex: number,
  perimeterEdgeIntersection: ClosestPointToPolyLine2,
  origin: Vec2,
  source?: PropLocationOnPerimeter,
) {
  const { edgeIndex } = perimeterEdgeIntersection;

  if (!targetSet[perimeterIndex]) {
    targetSet[perimeterIndex] = [];
  }

  if (!targetSet[perimeterIndex][edgeIndex]) {
    targetSet[perimeterIndex][edgeIndex] = [];
  }

  targetSet[perimeterIndex][edgeIndex].push({
    origin,
    perimeterIndex: perimeterIndex,
    perimeterEdgeIntersection: perimeterEdgeIntersection,
    source,
  });
}

function findClosestBoundedEdgeIntersection(
  pt: Vec2,
  polyline: PolyLine2,
  filter?: (x1: number, y1: number, x2: number, y2: number) => boolean,
): ClosestPointToPolyLine2 | null {
  const boundedEdgeIntersections = findBoundedEdgeIntersections(pt, polyline, true, true, filter);

  if (boundedEdgeIntersections.length) {
    return boundedEdgeIntersections[0];
  } else {
    return null;
  }
}

function findNextClosestBoundedEdgeIntersection(
  pt: Vec2,
  edgeIndex: number,
  polyline: PolyLine2,
  filter?: (x1: number, y1: number, x2: number, y2: number) => boolean,
): ClosestPointToPolyLine2 | null {
  const nextL = findNextBoundedEdgeIntersectionL(pt, edgeIndex, polyline, true, filter);
  const nextR = findNextBoundedEdgeIntersectionR(pt, edgeIndex, polyline, true, filter);

  if (!nextL || !nextR) {
    return null;
  } else if (nextL.dist < nextR.dist) {
    return nextL;
  } else {
    return nextR;
  }
}

function findBoundedEdgeIntersections(
  pt: Vec2,
  polyline: PolyLine2,
  sort: boolean = false,
  checkBounded: boolean = true,
  filter?: (x1: number, y1: number, x2: number, y2: number) => boolean,
) {
  const boundedEdgeIntersections: ClosestPointToPolyLine2[] = [];

  const edgeCount = polyLine2EdgeCount(polyline);

  for (let i = 0; i < edgeCount; i++) {
    const edgeIndex = i;
    const edge = polyLine2Edge(polyline, edgeIndex);
    const isEdgeBounded = checkBounded ? vec2IsInEdgeBounds(pt[0], pt[1], edge[0], edge[1], edge[2], edge[3]) : true;

    if (isEdgeBounded) {
      if (!filter || (filter && !filter(edge[0], edge[1], edge[2], edge[3]))) {
        const edgeIntersection = closestPointPolyLine2(edge, pt[0], pt[1]);
        boundedEdgeIntersections.push({ ...edgeIntersection, edgeIndex });
      }
    }
  }

  if (boundedEdgeIntersections.length === 0 && checkBounded) {
    return findBoundedEdgeIntersections(pt, polyline, sort, false, filter);
  }

  if (sort) {
    boundedEdgeIntersections.sort((a, b) => a.dist - b.dist);
  }

  return boundedEdgeIntersections;
}

function findNextBoundedEdgeIntersectionR(
  pt: Vec2,
  edgeIndex: number,
  polyline: PolyLine2,
  checkBounded: boolean = true,
  filter?: (x1: number, y1: number, x2: number, y2: number) => boolean,
): ClosestPointToPolyLine2 | null {
  const edgeCount = polyLine2EdgeCount(polyline);

  for (let i = 1; i < edgeCount; i++) {
    const nextEdgeIndex = (i + edgeIndex) % edgeCount;
    if (nextEdgeIndex === edgeIndex) {
      // went all the way around
      // likely this polyline should be simplified
      return null;
    }

    const edge = polyLine2Edge(polyline, nextEdgeIndex);
    const isBounded = checkBounded ? vec2IsInEdgeBounds(pt[0], pt[1], edge[0], edge[1], edge[2], edge[3]) : true;

    if (isBounded) {
      if (!filter || (filter && !filter(edge[0], edge[1], edge[2], edge[3]))) {
        const edgeIntersection = closestPointPolyLine2(edge, pt[0], pt[1]);
        return { ...edgeIntersection, edgeIndex: nextEdgeIndex } as ClosestPointToPolyLine2;
      }
    }
  }

  if (checkBounded) {
    return findNextBoundedEdgeIntersectionR(pt, edgeIndex, polyline, false, filter);
  }

  return null;
}

function findNextBoundedEdgeIntersectionL(
  pt: Vec2,
  edgeIndex: number,
  polyline: PolyLine2,
  checkBounded: boolean = true,
  filter?: (x1: number, y1: number, x2: number, y2: number) => boolean,
): ClosestPointToPolyLine2 | null {
  const edgeCount = polyLine2EdgeCount(polyline);

  for (let i = 1; i < edgeCount; i++) {
    const nextEdgeIndex = (edgeCount + edgeIndex - i) % edgeCount;
    if (nextEdgeIndex === edgeIndex) {
      // went all the way around
      // likely this polyline should be simplified
      return null;
    }

    const edge = polyLine2Edge(polyline, nextEdgeIndex);
    const isBounded = checkBounded ? vec2IsInEdgeBounds(pt[0], pt[1], edge[0], edge[1], edge[2], edge[3]) : true;

    if (isBounded) {
      if (!filter || (filter && !filter(edge[0], edge[1], edge[2], edge[3]))) {
        const edgeIntersection = closestPointPolyLine2(edge, pt[0], pt[1]);
        return { ...edgeIntersection, edgeIndex: nextEdgeIndex } as ClosestPointToPolyLine2;
      }
    }
  }

  if (checkBounded) {
    return findNextBoundedEdgeIntersectionL(pt, edgeIndex, polyline, false, filter);
  }

  return null;
}

function findPerimeterIndex(x: number, y: number, perimeterProfiles: PolyLine2[]): number {
  for (let i = 0; i < perimeterProfiles.length; i++) {
    if (classifyContainmentVec2PolyLine2Fast(x, y, perimeterProfiles[i])) {
      return i;
    }
  }
  return findNearestPerimeterIndex(x, y, perimeterProfiles);
}

function findNearestPerimeterIndex(x: number, y: number, perimeterProfiles: PolyLine2[]): number {
  let minDist: number = Number.POSITIVE_INFINITY;
  let index: number = -1;
  for (let i = 0; i < perimeterProfiles.length; i++) {
    const aabb = aabb2ByPolyLine2s([perimeterProfiles[i]]);
    assertIsNotNull(aabb);
    const [cx, cy] = aabb2Center(aabb);
    const dist = vec2DistSquared(x, y, cx, cy);
    if (dist < minDist) {
      minDist = dist;
      index = i;
    }
  }
  return index;
}

function findWallsOnPerimeterEdge(wallStrip: WallStrip, wallsToIgnore: Wall[]) {
  const wallIdsToIgnore = wallsToIgnore.reduce(
    (o, w) => {
      o[w.id] = true;
      return o;
    },
    {} as { [id: string]: true },
  );
  const wallsOnPerimeterEdge: Wall[] = [];

  for (const wall of wallStrip.walls) {
    if (wallIdsToIgnore[wall.id]) {
      continue;
    }
    if (isWallOnPerimeterEdge(wall)) {
      wallsOnPerimeterEdge.push(wall);
    }
  }

  return wallsOnPerimeterEdge;
}

function isWallOnPerimeterEdge(wall: Wall) {
  const { leftRoom, rightRoom } = getWallPath(wall);

  if (!leftRoom || !rightRoom) {
    return true;
  }

  return leftRoom.attributes.roomClass !== rightRoom.attributes.roomClass;
}

function areWallPathsConnected(p1: WallPath, p2: WallPath) {
  const startEq = p1.startX === p2.startX && p1.startY === p2.startY;
  const endEq = p1.endX === p2.endX && p1.endY === p2.endY;

  const invStartEq = p1.startX === p2.endX && p1.startY === p2.endY;
  const invEndEq = p1.endX === p2.startX && p1.endY === p2.startY;

  return startEq || endEq || invStartEq || invEndEq;
}

function areWallPathsRectilinear(p1: WallPath, p2: WallPath) {
  const [p1x, p1y] = vec2Sub(p1.startX, p1.startY, p1.endX, p1.endY);
  const [p2x, p2y] = vec2Sub(p2.startX, p2.startY, p2.endX, p2.endY);

  return Math.abs(vec2Dot(p1x, p1y, p2x, p2y)) <= ANGLE_EPSILON;
}
